/*
  Claire Powell: no size until the shopper picks one (24 Oct 2026).

  Prestige always has a selected variant, so the first available size used to look chosen and could be
  added by mistake from the main button, the sticky bar or the capri card. This keeps Prestige's variant
  logic as it is and only asks for a deliberate choice:
  - size buttons show no selected state until the shopper taps one (variant-picker[data-cp-size-chosen]);
  - a link that names a variant (?variant=, as Google Shopping uses) counts as a choice;
  - submitting a product form, or the capri card, without a chosen size shows "Please choose your size",
    scrolls the sizes into view and focuses the first one.
  The look depends on html.cp-size-js, which sections/main-product.liquid removes again if this file
  never runs, so a failed download falls back to today's behaviour.
*/
(() => {
  if (window.cpSizeGuard) return;

  const chosen = new Map(); // form id -> id of the size value the shopper picked

  const pickerFor = (formId) => document.querySelector(`variant-picker[form-id="${CSS.escape(formId)}"]`);
  const sizeSet = (picker) => (picker ? picker.querySelector('[data-cp-size-option]') : null);

  const needsSize = (formId) => Boolean(sizeSet(pickerFor(formId))) && !chosen.has(formId);

  function sync(formId) {
    const picker = pickerFor(formId);
    const fieldset = sizeSet(picker);
    if (!fieldset) return;

    const isChosen = chosen.has(formId);
    picker.toggleAttribute('data-cp-size-chosen', isChosen);

    if (isChosen) {
      const error = fieldset.querySelector('.cp-size-error');
      if (error) error.remove();
    }

    if (picker.getAttribute('context') === 'main_product') {
      document.documentElement.classList.toggle('cp-size-needed', !isChosen);
    }
  }

  function showError(formId) {
    const fieldset = sizeSet(pickerFor(formId));
    if (!fieldset) return;

    let error = fieldset.querySelector('.cp-size-error');
    if (!error) {
      error = document.createElement('p');
      error.className = 'cp-size-error';
      error.setAttribute('role', 'alert');
      const info = fieldset.querySelector('.variant-picker__option-info');
      if (info) info.after(error);
      else fieldset.prepend(error);
    }
    error.textContent = 'Please choose your size';

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    fieldset.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });

    const first = fieldset.querySelector('input[type="radio"]:not(:disabled)');
    if (first) first.focus({ preventScroll: true });
  }

  function choose(input) {
    const fieldset = input.closest('[data-cp-size-option]');
    const picker = fieldset ? fieldset.closest('variant-picker[form-id]') : null;
    if (!picker) return;

    const formId = picker.getAttribute('form-id');
    chosen.set(formId, input.value);
    sync(formId);
  }

  // Tapping the size that is already checked fires a click but no change, so both count as a choice.
  document.addEventListener('click', (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.type === 'radio') choose(input);
  });

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.type === 'radio') choose(input);
  });

  // Keyboard: Space or Enter on a size chooses it. Space on the size that is already checked fires no click
  // in Chrome, and Enter would otherwise submit the form, so both are handled here.
  document.addEventListener('keydown', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || !input.closest('[data-cp-size-option]')) return;

    if (event.key === 'Enter') {
      event.preventDefault();
      if (!input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      choose(input);
    } else if (event.key === ' ' || event.key === 'Spacebar') {
      choose(input);
    }
  });

  // Prestige replaces the picker after each variant change; put the shopper's state back on the new one.
  document.addEventListener('variant:change', (event) => {
    const form = event.target;
    const formId = form && form.getAttribute ? form.getAttribute('id') : null;
    if (formId) sync(formId);
  });

  // Runs before Prestige's own submit handler on the form (capture phase on the document).
  document.addEventListener('submit', (event) => {
    const form = event.target;
    const formId = form && form.getAttribute ? form.getAttribute('id') : null;
    if (!formId || !needsSize(formId)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    showError(formId);
  }, true);

  // On page load, a size named in the link (?variant=) is already a choice.
  document.querySelectorAll('variant-picker[form-id]').forEach((picker) => {
    const formId = picker.getAttribute('form-id');
    const fieldset = sizeSet(picker);
    if (!fieldset) return;

    if (picker.hasAttribute('data-cp-size-chosen')) {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (checked) chosen.set(formId, checked.value);
    }
    sync(formId);
  });

  window.cpSizeGuard = {
    needsSize,
    check(formId) {
      if (!needsSize(formId)) return true;
      showError(formId);
      return false;
    }
  };
})();
